import React from 'react';
import { Box, Typography, IconButton, Card, CardMedia, CircularProgress, Menu, MenuItem } from '@mui/material';
import { ReportImage } from '@models/DomainModels/Master/report-image';
import Grid from '@mui/material/Grid2';
import styles from './ImageCard.module.scss';
import { useTranslation } from 'react-i18next';
import EditOutlined from '@mui-symbols-material/w400/EditOutlined';
import DeleteOutlined from '@mui-symbols-material/w400/DeleteOutlined';
import VisibilityOutlined from '@mui-symbols-material/w400/VisibilityOutlined';
import MoreVertOutlined from '@mui-symbols-material/w400/MoreVertOutlined';
import { ReportImageDialogWrapper } from '../dialogs/ReportImageDialog';
import { DynamicStore } from '@state/interfaces/idynamic-store';

interface ImageCardProps {
  image: ReportImage;
  imageData?: string;
  isLoading: boolean;
  onLoadImage: (imageId: string) => void;
  section: 'header' | 'footer';
  organizationId: string;
  store?: DynamicStore;
  onDialogSuccess?: (imageId?: string) => void;
  onDelete?: (imageId: string) => void;
  onViewFullSize?: (image: ReportImage) => void;
}

/**
 * ImageCard component displays a report image with loading and options menu.
 *
 * @param image - The report image metadata
 * @param imageData - The image data as a base64 string or URL
 * @param isLoading - Whether the image is currently loading
 * @param onLoadImage - Callback to load the image data
 * @param section - The section of the image
 * @param organizationId - The organization ID
 * @param store - The store
 * @param onDialogSuccess - Callback when the dialog is successfully closed
 * @param onDelete - Callback when the image is deleted
 * @param onViewFullSize - Callback when the image is clicked to view in full size
 * @returns The rendered image card component
 */
const ImageCardComponent: React.FC<Readonly<ImageCardProps>> = ({
  image,
  imageData,
  isLoading,
  onLoadImage,
  section,
  organizationId,
  store,
  onDialogSuccess,
  onDelete,
  onViewFullSize
}) => {
  const { t } = useTranslation(['reportImages', 'common']);

  // State for context menu
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // State for ReportImageDialog
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  /**
   * Handles opening the context menu.
   * @param event - Mouse event from the IconButton
   */
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  /**
   * Handles closing the context menu.
   */
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  /**
   * Handles clicking the Edit menu item.
   */
  const handleEditClick = () => {
    handleMenuClose(); // Close the menu first
    // Ensure we have the image data before opening the dialog
    if (!imageData) {
      onLoadImage(image.id);
    }
    setIsDialogOpen(true);
  };

  /**
   * Handles closing the ReportImageDialog.
   */
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleDialogSuccess = () => {
    setIsDialogOpen(false);
    if (onDialogSuccess) onDialogSuccess(image.id);
  };

  /**
   * Handles clicking the Preview menu item.
   */
  const handlePreviewClick = () => {
    handleMenuClose(); // Close the menu first
    // Ensure we have the image data before opening the viewer
    if (!imageData) {
      onLoadImage(image.id);
    }
    if (onViewFullSize) {
      onViewFullSize(image);
    }
  };

  /**
   * Handles clicking the Delete menu item.
   */
  const handleDeleteClick = () => {
    handleMenuClose(); // Close the menu first
    if (onDelete) {
      onDelete(image.id);
    }
  };

  const renderImageContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (imageData) {
      return (
        <CardMedia
          component="img"
          image={imageData}
          alt={image.name}
          className={styles.cardMedia}
          loading="lazy"
          onClick={() => onViewFullSize?.(image)}
          style={{ cursor: onViewFullSize ? 'pointer' : 'default' }}
        />
      );
    }

    return (
      <button type="button" className={styles.placeholderButton} aria-label={t('common:clickToLoad')} onClick={() => onLoadImage(image.id)}>
        <Typography variant="caption">{t('common:clickToLoad')}</Typography>
      </button>
    );
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
      <Card className={styles.imageCard} sx={{ marginBottom: '8px' }} data-testid={`image-card-${image.name.replace(/ /g, '-')}`}>
        {/* Main content with image */}
        <Box sx={{ p: 0 }}>
          <div className={styles.cardMediaContainer}>{renderImageContent()}</div>
        </Box>

        {/* Footer with name and controls */}
        <Box className={styles.cardFooter}>
          <Box className={styles.nameContainer}>
            <Typography variant="body2" className={styles.imageNameText} title={image.name}>
              {image.name}
            </Typography>
          </Box>
          <IconButton
            aria-label={t('moreOptions')}
            size="small"
            aria-controls={isMenuOpen ? `image-card-menu-${image.id}` : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
            onClick={handleMenuOpen}
            data-testid="more-options-button">
            <MoreVertOutlined fontSize="small" />
          </IconButton>
          <Menu
            id={`image-card-menu-${image.id}`}
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            slotProps={{
              list: {
                'aria-labelledby': `image-card-menu-button-${image.id}`,
                role: 'menu'
              }
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            data-testid="image-card-menu">
            <MenuItem onClick={handlePreviewClick} data-testid="preview-button">
              <VisibilityOutlined fontSize="small" aria-hidden="true" />
              <Box ml={1}>{t('reportImages.preview')}</Box>
            </MenuItem>
            <MenuItem onClick={handleEditClick} data-testid="edit-button">
              <EditOutlined fontSize="small" aria-hidden="true" />
              <Box ml={1}>{t('reportImages.edit')}</Box>
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} data-testid="delete-button">
              <DeleteOutlined fontSize="small" aria-hidden="true" />
              <Box ml={1}>{t('reportImages.delete')}</Box>
            </MenuItem>
          </Menu>
        </Box>
      </Card>
      {/* ReportImageDialogWrapper for editing */}
      <ReportImageDialogWrapper
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        initialData={
          imageData
            ? {
                ...image,
                url: imageData
              }
            : image
        }
        section={section}
        organizationId={organizationId}
        store={store}
        dialogMode="edit"
      />
    </Grid>
  );
};

export const ImageCard = React.memo(ImageCardComponent);
